import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import {
    GET_MATM_ORDER_LIST_API,
    POST_MATM_ORDER_STATUS_API
} from './../../Utils/appConstants';
import {
    getExportFormat,
    getUTCToLocalDateTimeFormat,
    toDayDate,
    queryDate
} from './../../Utils/function';
import { getRequest, postRequest } from './../../Utils/AppApi';
import { INRFormat, matmOrderStatus } from './../../Utils/Common';
import { useHistory } from "react-router-dom";
import { DatePicker, Tooltip, Modal, Input } from 'antd';
import { toast } from 'react-toastify';

const { TextArea } = Input;

function PurchaseList() {

    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';

    const [searchUserId, setSearchUserId] = useState(null);
    const [endDate, setEndDate] = useState(toDayDate());
    const [startDate, setStartDate] = useState(toDayDate());
    const [getData, setData] = useState([]);
    const [getCsvData, setCsvData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(limit);

    // ---- Status modal state ----
    const [isModal, setModal] = useState(false);
    const [isComment, setComment] = useState('');
    const [isSelectData, setSelectData] = useState({});
    const [isStatus, setStatus] = useState('');     // '1' | '2' | '3'
    const [isLoading, setLoading] = useState(false);

    // -------------------- Columns --------------------
    const columns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "200px"
        },
        {
            name: 'Transaction Id',
            selector: row => row.transactionId,
            width: "180px"
        },
        {
            name: 'Name',
            selector: row => row.name,
            width: "180px"
        }, {
            name: 'Mobile',
            selector: row => <Tooltip placement="bottom" title={row.mobile}>{row.mobile}</Tooltip>,
            width: "150px"
        }, {
            name: 'Product',
            selector: row => <Tooltip placement="bottom" title={row.product}>{row.product}</Tooltip>,
            width: "180px"
        }, {
            name: 'Qty',
            selector: row => <Tooltip placement="bottom" title={row.quantity}>{row.quantity}</Tooltip>,
            width: "60px"
        }, {
            name: 'Amount',
            selector: row => INRFormat(row.amount),
            width: "100px"
        },
        {
            name: 'Parent ID',
            selector: row => (
                <Tooltip placement="bottom" title={row.parentUsers?.username}>
                    {row.parentUsers?.username}
                </Tooltip>
            ),
            width: "130px"
        },
        {
            name: 'User ID',
            selector: row => (
                <Tooltip placement="bottom" title={row.users?.username + ' / ' + row.users?.name}>
                    {row.users?.username + ' / ' + row.users?.name}
                </Tooltip>
            ),
            width: "220px"
        }, {
            name: 'Status',
            cell: row => {
                const s = row.orderStatus == null ? "0" : String(row.orderStatus);
                const map = {
                    "0": { text: 'Pending', color: '#f0ad4e' },   // amber
                    "1": { text: 'Accepted', color: '#28a745' },   // green
                    "2": { text: 'Rejected', color: '#dc3545' },   // red
                    "3": { text: 'Hold', color: '#17a2b8' },   // teal
                };
                const v = map[s] || { text: '-', color: '#6c757d' };
                return (
                    <span style={{ fontSize: '14px', fontWeight: 600, color: v.color }}>
                        {v.text}
                    </span>
                );
            },
            width: "130px"
        }, {
            name: 'Action',
            cell: row => {
                const s = row.orderStatus == null ? "0" : String(row.orderStatus);

                // Accepted / Rejected → no actions
                if (s === "1" || s === "2") {
                    return <span style={{ color: '#999' }}>-</span>;
                }

                // Pending → all 3 buttons
                if (s === "0") {
                    return (
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => openStatusModal(row, "1")}
                            >
                                Accept
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => openStatusModal(row, "2")}
                            >
                                Reject
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-warning"
                                onClick={() => openStatusModal(row, "3")}
                            >
                                Hold
                            </button>
                        </div>
                    );
                }

                // Hold → Accept + Reject only
                if (s === "3") {
                    return (
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => openStatusModal(row, "1")}
                            >
                                Accept
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => openStatusModal(row, "2")}
                            >
                                Reject
                            </button>
                        </div>
                    );
                }

                return <span style={{ color: '#999' }}>-</span>;
            },
            width: "240px",
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }, {
            name: 'Remark',
            selector: row => <Tooltip placement="bottom" title={row.comment}>{row.comment}</Tooltip>,
            width: "220px"
        },
    ];

    // -------------------- Status Handlers --------------------
    // Opens the remark modal for Accept / Reject / Hold — all statuses.
    const openStatusModal = (row, status) => {
        setSelectData(row);
        setStatus(status);
        setComment(row.comment || "");
        setModal(true);
    };

    // Actual API call
    const submitStatus = (row, status, comment) => {
        setLoading(true);
        const payload = { id: row._id, status: status, comment: comment || "" };

        postRequest(`${POST_MATM_ORDER_STATUS_API}`, payload, history)
            .then((response) => {
                setLoading(false);
                setModal(false);
                if (response.success === true) {
                    getDataReports();
                    toast.success(response.message);
                } else {
                    toast.error(response.message);
                }
            })
            .catch(function (error) {
                setLoading(false);
                toast.error("Something went wrong");
            });
    };

    // -------------------- Helpers --------------------
    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    };

    const params = { page: page, limit: isLimit };

    const getDataReports = (data) => {
        if (data) {
            setLimit(data);
            params.limit = data;
        }
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchUserId = searchUserId ? searchUserId : "";

        const queryString = objectToQueryString(params);
        getRequest(`${GET_MATM_ORDER_LIST_API}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    setData(response.data);
                    setTotalRecord(response.data.totalDocs);
                    setPage(response.data.page);
                } else {
                    setData([]);
                    setTotalRecord(0);
                    setPage(1);
                }
            })
            .catch(function (error) {
                console.log(error);
            });
    }; // eslint-disable-line react-hooks/exhaustive-deps

    const getCSVDataList = () => {
        let params = {};
        params.exportsCsv = true;
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchUserId = searchUserId ? searchUserId : "";

        const queryString = objectToQueryString(params);
        getRequest(`${GET_MATM_ORDER_LIST_API}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    let csvdataRecord = [];
                    let csvdata = response.data;
                    csvdata.map((e) => {
                        return csvdataRecord.push({
                            "Date Time": getExportFormat(e.createdAt),
                            "name": e.name,
                            "Mobile": e.mobile,
                            "Product": e.product,
                            "Quantity": e.quantity,
                            "Amount": e.amount,
                            // "Total Amount": e.totalAmount,
                            "Parent Id": e.parentUsers?.username,
                            "User Id": e.users?.username + ' / ' + e.users?.name,
                            "Status": matmOrderStatus(e.orderStatus),
                            "Remark": e.comment,
                        });
                    });
                    setCsvData(csvdataRecord);
                    setTimeout(() => {
                        csvLink.current.link.click();
                    }, 1000);
                } else {
                    setCsvData([]);
                }
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    const handlePageChange = (page, size) => {
        setPage(page);
    };

    const onChangeStartDate = (date, dateString) => {
        setStartDate(date);
    };

    const onChangeEndDate = (date, dateString) => {
        setEndDate(date);
    };

    const handleClear = () => {
        setStartDate("");
        setEndDate("");
        setSearchUserId('');
    };

    const handleSearch = () => {
        getDataReports();
    };

    useEffect(() => {
        getDataReports();
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    // -------------------- Render --------------------
    return (
        <div>
            <div className="content-wrapper pt-2">
                <div className="content-header my-0 py-0">
                    <div className="container-fluid">
                        <div className="row mb-0 align-items-center">
                            <div className="col-sm-6">
                                <h1 className='page-title text-blue font-merriweather'>
                                    Purchase <span className='text-red'>List</span>
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content">
                    <div className="container-fluid pt-2 pb-5">
                        <div className='card col-md-12'>
                            <div className='card-body'>

                                {/* ---------- Filters ---------- */}
                                <div className="row">
                                    <div className='col-md-10 date-search mb-0'>
                                        <div className='row'>
                                            <div className='col-md-4 col-lg-4 d-flex'>
                                                <div className="form-group mr-3">
                                                    <label className='date-search-label mb-0 text-blue'>From</label>
                                                    <DatePicker
                                                        className="form-control form-control-border"
                                                        value={startDate}
                                                        format={dateFormat}
                                                        onChange={onChangeStartDate}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className='date-search-label mb-0 text-red'>To</label>
                                                    <DatePicker
                                                        className="form-control form-control-border"
                                                        value={endDate}
                                                        format={dateFormat}
                                                        onChange={onChangeEndDate}
                                                    />
                                                </div>
                                            </div>
                                            <div className='col-md-2 col-lg-2'>
                                                <div className="form-group">
                                                    <label className='date-search-label mb-0 text-red'></label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-border"
                                                        placeholder="User ID"
                                                        onChange={(e) => setSearchUserId(e.target.value)}
                                                        value={searchUserId}
                                                    />
                                                </div>
                                            </div>
                                            <div className='col-md-1 col-lg-1'>
                                                <div><label className='date-search-label mb-0 text-red'>&nbsp;</label></div>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary rounded-pill px-3"
                                                    onClick={() => handleSearch()}
                                                >
                                                    Search
                                                </button>
                                            </div>
                                            <div className='col-md-1 col-lg-1'>
                                                <div><label className='date-search-label mb-0 text-red'>&nbsp;</label></div>
                                                <button
                                                    type="button"
                                                    className="btn btn-default rounded-pill px-4"
                                                    onClick={() => handleClear()}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='col-md-2'>
                                        <div className='float-sm-right'>
                                            <button
                                                type="button"
                                                className="btn btn-primary mr-3"
                                                onClick={() => getCSVDataList()}
                                            >
                                                <i className="fa fa-file-excel"></i> Export
                                            </button>
                                            <CSVLink
                                                data={getCsvData}
                                                filename='purchase-list'
                                                className='hidden'
                                                ref={csvLink}
                                                target='_blank'
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* ---------- Table ---------- */}
                                <div className="container-fluid pt-2 pb-5">
                                    <DataTable
                                        title={""}
                                        columns={columns}
                                        data={getData.docs}
                                        highlightOnHover
                                        pagination
                                        paginationServer
                                        paginationTotalRows={getTotalRecord}
                                        onChangeRowsPerPage={getDataReports}
                                        onChangePage={handlePageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---------- Remark Modal for Accept / Reject / Hold ---------- */}
            <Modal
                title={
                    isStatus === "1"
                        ? "Accept Order"
                        : isStatus === "2"
                            ? "Reject Order"
                            : "Put Order on Hold"
                }
                open={isModal}
                onCancel={() => setModal(false)}
                footer={[
                    <button
                        key="cancel"
                        type="button"
                        className="btn btn-default mr-2"
                        onClick={() => setModal(false)}
                    >
                        Cancel
                    </button>,
                    <button
                        key="submit"
                        type="button"
                        className={
                            isStatus === "1"
                                ? "btn btn-success"
                                : isStatus === "2"
                                    ? "btn btn-danger"
                                    : "btn btn-warning"
                        }
                        disabled={isLoading}
                        onClick={() => submitStatus(isSelectData, isStatus, isComment)}
                    >
                        {isLoading ? "Please wait..." : "Submit"}
                    </button>,
                ]}
            >
                <div className="form-group">
                    <label style={{ fontWeight: 600 }}>
                        Remark{" "}
                        {isStatus === "1"
                            ? "(optional)"
                            : isStatus === "2"
                                ? "(reason for rejection)"
                                : "(reason for hold)"}
                    </label>
                    <TextArea
                        rows={4}
                        value={isComment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Enter remark (optional)"
                        maxLength={500}
                        showCount
                    />
                </div>
            </Modal>
        </div>
    );
}

export default PurchaseList;